import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Create,
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  NumberInput,
  TextInput,
  DateInput,
  SelectInput,
  required,
  minValue,
} from "react-admin";
import type { CreateProps } from "react-admin";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { redirectToProjectModuleDashboard } from "@/containers/projects/redirect-to-project-module-dashboard";
import { useRouteId } from "@/utils/route-utility";
import { formatINR } from "@/utils";
import { PAYMENT_MODE_CONST } from "@/constants";

const paymentChoices = Object.entries(PAYMENT_MODE_CONST).map(([id, name]) => ({ id, name }));

export const ProjectTakhmeenReceiptCreate = (props: CreateProps) => {
  const [searchParams] = useSearchParams();
  const projectId = useRouteId();
  const initialTakhmeenId = useMemo(
    () => searchParams.get("projectTakhmeenId") ?? undefined,
    [searchParams]
  );
  const redirectTo = useMemo(
    () => redirectToProjectModuleDashboard({ routeProjectId: projectId, fallback: "list" }),
    [projectId]
  );

  if (!projectId) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          Open a project from the Projects list to record a receipt.
        </Typography>
      </Box>
    );
  }

  return (
    <Create
      {...props}
      redirect={redirectTo}
      transform={(data: Record<string, unknown>) => ({
        projectTakhmeenId: data.projectTakhmeenId,
        amount: Number(data.amount),
        paymentMode: data.paymentMode,
        paymentRef:
          typeof data.paymentRef === "string"
            ? data.paymentRef.trim() || undefined
            : data.paymentRef,
        receiptDate: data.receiptDate,
        remarks: typeof data.remarks === "string" ? data.remarks.trim() || undefined : data.remarks,
      })}
    >
      <SimpleForm
        defaultValues={{ projectTakhmeenId: initialTakhmeenId, receiptDate: new Date() }}
      >
        <ReferenceInput
          source="projectTakhmeenId"
          reference="projectTakhmeens"
          filter={{ projectId }}
          perPage={100}
        >
          <AutocompleteInput
            optionText={(r: {
              memberName?: string | null;
              itsNo?: string | null;
              committedAmount?: number | null;
            }) => {
              const name = r.memberName ?? r.itsNo ?? "—";
              const its = r.itsNo ?? "";
              const amt = formatINR(r.committedAmount, { empty: "—" });
              return `${name}${its ? ` (${its})` : ""} — ${amt}`;
            }}
            label="Takhmeen pledge"
            validate={required()}
          />
        </ReferenceInput>
        <NumberInput source="amount" validate={[required(), minValue(1)]} />
        <SelectInput source="paymentMode" choices={paymentChoices} validate={required()} />
        <TextInput source="paymentRef" fullWidth />
        <DateInput source="receiptDate" label="Receipt date" validate={required()} />
        <TextInput source="remarks" multiline fullWidth />
      </SimpleForm>
    </Create>
  );
};
